import React, { useEffect, useMemo, useState } from "react";

import {
  FaClipboard,
  FaCheck,
  FaFilter,
  FaRedo,
  FaTable,
} from "react-icons/fa";

import regrouperParTranchesDages from "../../../../../utils/tranchesDages";
import { getCommunityStats } from "../../../../../services/dashboardServices";
import { getSettings } from "../../../../../services/settingsServices";

const TRANCHES_ORDER = [
  "0-3",
  "4-5",
  "6-11",
  "12-17",
  "18-25",
  "26-45",
  "46-59",
  "60-74",
  "75-100",
];

const ALL = "_ALL";

function extractArray(data) {
  if (Array.isArray(data)) return data;

  if (data && typeof data === "object") {
    if (Array.isArray(data.rows)) return data.rows;
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.data)) return data.data;
    if (Array.isArray(data.communes)) return data.communes;
  }

  return [];
}

function unwrapSettings(settings) {
  if (!settings) return {};

  let output = settings;

  if (Array.isArray(output)) output = output[0] || {};
  if (output?.dataValues) output = output.dataValues;

  return output || {};
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString("fr-FR");
}

function getFMValue(fm, gender) {
  if (!fm) return 0;

  if (gender === "M") {
    return Number(fm.M || fm.Masculin || fm.masculin || 0);
  }

  if (gender === "F") {
    return Number(fm.F || fm.Féminin || fm.Feminin || fm.feminin || 0);
  }

  return 0;
}

function copyTextFallback(text) {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "absolute";
  textarea.style.left = "-9999px";

  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
}

function Skeleton() {
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <div className="h-5 w-80 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
          <div className="mt-2 h-3 w-96 max-w-full animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
        </div>

        <div className="h-10 w-40 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
      </div>

      <div className="h-20 animate-pulse rounded-[24px] bg-slate-200 dark:bg-slate-800" />
      <div className="h-80 animate-pulse rounded-[24px] bg-slate-200 dark:bg-slate-800" />
    </div>
  );
}

function ErrorPanel({ message, onRetry }) {
  return (
    <div className="rounded-[24px] border border-rose-200 bg-rose-50 p-5 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
      <p className="font-bold">Erreur de chargement</p>

      <p className="mt-1 text-sm leading-6">{message}</p>

      <button
        type="button"
        onClick={onRetry}
        className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-rose-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-rose-700"
      >
        <FaRedo />
        Réessayer
      </button>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-[24px] border border-dashed border-slate-300 bg-slate-50 p-8 text-center dark:border-slate-700 dark:bg-slate-950/50">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
        <FaTable />
      </div>

      <h3 className="mt-4 text-lg font-bold text-slate-900 dark:text-white">
        Aucune donnée à afficher
      </h3>

      <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
        Aucune statistique ne correspond aux filtres actuels.
      </p>
    </div>
  );
}

function MiniStat({ label, value, helper, accent = "indigo" }) {
  const accents = {
    indigo:
      "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    emerald:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
    amber:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
    rose:
      "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300",
  };

  return (
    <div className={`rounded-2xl border px-4 py-3 ${accents[accent]}`}>
      <p className="text-[11px] font-bold uppercase tracking-[0.16em] opacity-75">
        {label}
      </p>

      <p className="mt-0.5 text-xl font-bold text-slate-950 dark:text-white">
        {value}
      </p>

      {helper ? (
        <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-300">
          {helper}
        </p>
      ) : null}
    </div>
  );
}

export default function CommunityStatisticsTable() {
  const [raw, setRaw] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [communes, setCommunes] = useState([]);

  const [selectedCommune, setSelectedCommune] = useState(ALL);
  const [selectedTranche, setSelectedTranche] = useState(ALL);

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [copied, setCopied] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      setErrorMsg("");

      const [communityStatsResponse, settingsResponse] = await Promise.all([
        getCommunityStats(),
        getSettings(),
      ]);

      const communityStats = extractArray(communityStatsResponse);
      const settings = unwrapSettings(settingsResponse);

      const uniqueCommunes = Array.from(
        new Set(
          communityStats
            .map((item) => item?.commune)
            .filter(Boolean)
            .map((commune) => String(commune).trim())
        )
      ).sort((a, b) => String(a).localeCompare(String(b), "fr"));

      const clubCommune = settings?.commune_club || "";
      const defaultCommune = uniqueCommunes.includes(clubCommune)
        ? clubCommune
        : uniqueCommunes[0] || ALL;

      setCommunes(uniqueCommunes);
      setSelectedCommune(defaultCommune);
      setRaw(communityStats);
      setGrouped(regrouperParTranchesDages(communityStats || []));
    } catch (error) {
      console.error("Erreur lors du chargement des stats communes :", error);
      setRaw([]);
      setGrouped({});
      setCommunes([]);
      setErrorMsg(
        "Impossible de charger les statistiques par commune, âge et sexe."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const tranches = useMemo(() => {
    const keys = Object.keys(grouped || {});
    return TRANCHES_ORDER.filter((tranche) => keys.includes(tranche));
  }, [grouped]);

  const tableRows = useMemo(() => {
    const selectedTranches =
      selectedTranche === ALL ? tranches : [selectedTranche];

    return selectedTranches.map((tranche) => {
      const trancheData = grouped?.[tranche] || {};

      let communeMasculin = 0;
      let communeFeminin = 0;
      let horsCommuneMasculin = 0;
      let horsCommuneFeminin = 0;

      Object.entries(trancheData).forEach(([commune, fm]) => {
        const masculin = getFMValue(fm, "M");
        const feminin = getFMValue(fm, "F");

        if (selectedCommune === ALL || commune === selectedCommune) {
          communeMasculin += masculin;
          communeFeminin += feminin;
        } else {
          horsCommuneMasculin += masculin;
          horsCommuneFeminin += feminin;
        }
      });

      const total =
        communeMasculin +
        communeFeminin +
        horsCommuneMasculin +
        horsCommuneFeminin;

      return {
        tranche,
        communeMasculin,
        communeFeminin,
        horsCommuneMasculin,
        horsCommuneFeminin,
        total,
      };
    });
  }, [grouped, selectedCommune, selectedTranche, tranches]);

  const totals = useMemo(() => {
    return tableRows.reduce(
      (acc, row) => {
        acc.communeMasculin += row.communeMasculin;
        acc.communeFeminin += row.communeFeminin;
        acc.horsCommuneMasculin += row.horsCommuneMasculin;
        acc.horsCommuneFeminin += row.horsCommuneFeminin;
        acc.total += row.total;
        return acc;
      },
      {
        communeMasculin: 0,
        communeFeminin: 0,
        horsCommuneMasculin: 0,
        horsCommuneFeminin: 0,
        total: 0,
      }
    );
  }, [tableRows]);

  const empty = tableRows.every((row) => row.total === 0);

  const copyTableToClipboard = async () => {
    const communeLabel =
      selectedCommune === ALL ? "Toutes communes" : selectedCommune;

    const header = [
      "Tranche d'âge",
      `${communeLabel} - Masculin`,
      `${communeLabel} - Féminin`,
      selectedCommune === ALL
        ? "Hors sélection - Masculin"
        : `Hors ${communeLabel} - Masculin`,
      selectedCommune === ALL
        ? "Hors sélection - Féminin"
        : `Hors ${communeLabel} - Féminin`,
      "Total",
    ];

    const rows = tableRows.map((row) => [
      `${row.tranche} ans`,
      row.communeMasculin,
      row.communeFeminin,
      row.horsCommuneMasculin,
      row.horsCommuneFeminin,
      row.total,
    ]);

    const totalRow = [
      "Total",
      totals.communeMasculin,
      totals.communeFeminin,
      totals.horsCommuneMasculin,
      totals.horsCommuneFeminin,
      totals.total,
    ];

    const text = [header, ...rows, totalRow]
      .map((line) => line.join("\t"))
      .join("\n");

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        copyTextFallback(text);
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (error) {
      console.error("Erreur copie tableau :", error);
      copyTextFallback(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    }
  };

  if (loading) return <Skeleton />;

  if (errorMsg) {
    return <ErrorPanel message={errorMsg} onRetry={fetchData} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
              <FaTable />
            </span>

            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                Communes, sexe & tranches d’âge
              </h3>

              <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
                Tableau prêt à copier pour vos demandes de subvention mairie.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={copyTableToClipboard}
            disabled={empty}
            className={`inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-bold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60 ${
              copied
                ? "bg-emerald-600 text-white"
                : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            }`}
          >
            {copied ? <FaCheck /> : <FaClipboard />}
            {copied ? "Copié" : "Copier le tableau"}
          </button>

          <button
            type="button"
            onClick={fetchData}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaRedo className={loading ? "animate-spin" : ""} />
            Actualiser
          </button>
        </div>
      </div>

      <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/50">
        <div className="grid gap-3 lg:grid-cols-[2fr_1fr]">
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
              <FaFilter className="mr-1 inline" />
              Commune de référence
            </label>

            <select
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              value={selectedCommune}
              onChange={(event) => setSelectedCommune(event.target.value)}
            >
              <option value={ALL}>Toutes les communes</option>

              {communes.map((commune) => (
                <option key={commune} value={commune}>
                  {commune}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
              Tranche d’âge
            </label>

            <select
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              value={selectedTranche}
              onChange={(event) => setSelectedTranche(event.target.value)}
            >
              <option value={ALL}>Toutes les tranches</option>

              {tranches.map((tranche) => (
                <option key={tranche} value={tranche}>
                  {tranche} ans
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <MiniStat
          label={
            selectedCommune === ALL
              ? "Toutes communes H"
              : `${selectedCommune} H`
          }
          value={formatNumber(totals.communeMasculin)}
          helper="Masculin"
          accent="indigo"
        />

        <MiniStat
          label={
            selectedCommune === ALL
              ? "Toutes communes F"
              : `${selectedCommune} F`
          }
          value={formatNumber(totals.communeFeminin)}
          helper="Féminin"
          accent="rose"
        />

        <MiniStat
          label={selectedCommune === ALL ? "Hors sélection H" : "Hors commune H"}
          value={formatNumber(totals.horsCommuneMasculin)}
          helper="Masculin"
          accent="amber"
        />

        <MiniStat
          label={selectedCommune === ALL ? "Hors sélection F" : "Hors commune F"}
          value={formatNumber(totals.horsCommuneFeminin)}
          helper="Féminin"
          accent="emerald"
        />

        <MiniStat
          label="Total"
          value={formatNumber(totals.total)}
          helper="Tous profils"
          accent="indigo"
        />
      </div>

      {empty ? (
        <EmptyState />
      ) : (
        <div className="overflow-x-auto rounded-[24px] border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950/60">
          <table className="min-w-[980px] w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-900">
              <tr className="text-left">
                <th className="sticky left-0 z-10 bg-slate-50 px-4 py-3 font-bold text-slate-700 dark:bg-slate-900 dark:text-slate-200">
                  Tranche d’âge
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  {selectedCommune === ALL
                    ? "Toutes communes"
                    : selectedCommune}{" "}
                  — Masculin
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  {selectedCommune === ALL
                    ? "Toutes communes"
                    : selectedCommune}{" "}
                  — Féminin
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  {selectedCommune === ALL
                    ? "Hors sélection"
                    : `Hors ${selectedCommune}`}{" "}
                  — Masculin
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  {selectedCommune === ALL
                    ? "Hors sélection"
                    : `Hors ${selectedCommune}`}{" "}
                  — Féminin
                </th>

                <th className="px-4 py-3 text-right font-bold text-slate-700 dark:text-slate-200">
                  Total
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {tableRows.map((row) => (
                <tr
                  key={row.tranche}
                  className="transition hover:bg-slate-50 dark:hover:bg-slate-900"
                >
                  <td className="sticky left-0 z-10 bg-white px-4 py-3 font-bold text-slate-900 dark:bg-slate-950 dark:text-white">
                    {row.tranche} ans
                  </td>

                  <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                    {formatNumber(row.communeMasculin)}
                  </td>

                  <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                    {formatNumber(row.communeFeminin)}
                  </td>

                  <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                    {formatNumber(row.horsCommuneMasculin)}
                  </td>

                  <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                    {formatNumber(row.horsCommuneFeminin)}
                  </td>

                  <td className="px-4 py-3 text-right font-bold text-slate-900 dark:text-white">
                    {formatNumber(row.total)}
                  </td>
                </tr>
              ))}

              <tr className="bg-slate-50 dark:bg-slate-900">
                <td className="sticky left-0 z-10 bg-slate-50 px-4 py-3 font-bold text-slate-900 dark:bg-slate-900 dark:text-white">
                  Total
                </td>

                <td className="px-4 py-3 font-bold text-slate-900 dark:text-white">
                  {formatNumber(totals.communeMasculin)}
                </td>

                <td className="px-4 py-3 font-bold text-slate-900 dark:text-white">
                  {formatNumber(totals.communeFeminin)}
                </td>

                <td className="px-4 py-3 font-bold text-slate-900 dark:text-white">
                  {formatNumber(totals.horsCommuneMasculin)}
                </td>

                <td className="px-4 py-3 font-bold text-slate-900 dark:text-white">
                  {formatNumber(totals.horsCommuneFeminin)}
                </td>

                <td className="px-4 py-3 text-right font-bold text-slate-900 dark:text-white">
                  {formatNumber(totals.total)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs leading-5 text-slate-500 dark:text-slate-400">
        Le bouton “Copier le tableau” copie les données au format tabulé : vous
        pouvez ensuite les coller directement dans Excel, Google Sheets,
        LibreOffice ou un document de demande de subvention.
      </p>
    </div>
  );
}